import React, { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FaTimes } from 'react-icons/fa'
import { IconButton } from '../Button'
import { cx } from '../cx'
import s from './styles.module.scss'

const useEscape = (open: boolean, onClose?: () => void) => {
    useEffect(() => {
        if (!open || !onClose) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])
}

const useBodyLock = (open: boolean) => {
    useEffect(() => {
        if (!open) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [open])
}

export interface ModalProps {
    open: boolean
    onClose: () => void
    title?: ReactNode
    subtitle?: ReactNode
    /** Buttons for the footer; usually a neutral Cancel and one primary. */
    footer?: ReactNode
    size?: 'sm' | 'md' | 'lg' | 'xl'
    /** Remove body padding for edge-to-edge tables. */
    padding?: 'md' | 'none'
    /** Clicking the backdrop closes the modal (default true). */
    closeOnBackdrop?: boolean
    /** Shows a translucent spinner over the content. */
    busy?: boolean
    className?: string
    children?: ReactNode
}

/** Centered dialog. Renders into `document.body`. */
export const Modal = ({
    open,
    onClose,
    title,
    subtitle,
    footer,
    size = 'md',
    padding = 'md',
    closeOnBackdrop = true,
    busy = false,
    className,
    children,
}: ModalProps) => {
    useEscape(open, onClose)
    useBodyLock(open)
    if (!open) return null

    return createPortal(
        <div className={s.overlay} onMouseDown={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose()}>
            <div className={cx(s.panel, s[size], className)} role="dialog" aria-modal="true">
                {(title || subtitle) && (
                    <div className={s.head}>
                        <div className={s.headText}>
                            {title && <h2 className={s.title}>{title}</h2>}
                            {subtitle && <p className={s.subtitle}>{subtitle}</p>}
                        </div>
                        <IconButton label="Close" icon={<FaTimes />} variant="ghost" size="sm" onClick={onClose} />
                    </div>
                )}
                <div className={cx(s.body, padding === 'none' && s.padNone)}>{children}</div>
                {footer && <div className={s.foot}>{footer}</div>}
                {busy && (
                    <div className={s.veil} aria-busy="true">
                        <span />
                    </div>
                )}
            </div>
        </div>,
        document.body
    )
}

export interface DrawerProps {
    open: boolean
    onClose: () => void
    title?: ReactNode
    subtitle?: ReactNode
    /** Header controls (export, refresh) to the left of the close button. */
    actions?: ReactNode
    footer?: ReactNode
    width?: 'narrow' | 'default' | 'wide'
    padding?: 'md' | 'none'
    busy?: boolean
    className?: string
    children?: ReactNode
}

/** Right-hand slide-over for detail views that keep the list visible behind. */
export const Drawer = ({
    open,
    onClose,
    title,
    subtitle,
    actions,
    footer,
    width = 'default',
    padding = 'md',
    busy = false,
    className,
    children,
}: DrawerProps) => {
    useEscape(open, onClose)
    useBodyLock(open)
    if (!open) return null

    return createPortal(
        <>
            <div className={s.drawerOverlay} onMouseDown={onClose} />
            <aside className={cx(s.drawer, width === 'narrow' && s.narrow, width === 'wide' && s.wide, className)} role="dialog" aria-modal="true">
                <div className={s.head}>
                    <div className={s.headText}>
                        {title && <h2 className={s.title}>{title}</h2>}
                        {subtitle && <p className={s.subtitle}>{subtitle}</p>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {actions}
                        <IconButton label="Close" icon={<FaTimes />} variant="ghost" size="sm" onClick={onClose} />
                    </div>
                </div>
                <div className={cx(s.body, padding === 'none' && s.padNone)}>{children}</div>
                {footer && <div className={s.foot}>{footer}</div>}
                {busy && (
                    <div className={s.veil} aria-busy="true">
                        <span />
                    </div>
                )}
            </aside>
        </>,
        document.body
    )
}
